import {
    IField,
    V2,
    generateID,
    getObjectById,
    structuredCloneL,
} from '@eventr/common'
import {
    ColoursObj,
    ErrorWrapper,
    Gutters,
    MainButton,
    MobileConfirmDialog,
    TDialogStore,
    useDialogStore,
} from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import { ISubPage } from '../../../data/Data'
import { HeaderSearchBar } from '../../../home/components/carousel/slide/footer/HeaderSearchBar'
import i18n from '../../../i18n/i18next'
import { bs, useBoundStore } from '../../../state/bound.store'
import { query } from '../../../state/headerState/headerSignals'
import { RendererLocator } from '../../../v2/renderers/rendererLocator'
import { ScreenTypeProvider } from '../../../v2/state/screenTypeProvider'
import { ManageBookingsButton } from '../../hotelBooking/comps/manageBookingsButton'
import { NavigationView } from '../../layout/NavigationView'
import { ToolbarItemProps } from '../../layout/TopToolbar'
import { useEditSectionContext } from '../editSectionProvider'
interface IListSubPageProps {
    field: IField
    subPage: ISubPage
    _id?: string
}
export const ListSubPage = ({ subPage, field, _id }: IListSubPageProps) => {
    const {
        getItinTitle,
        getValue,
        section,
        createNewItem,
        updateValues,
        values,
    } = useEditSectionContext()
    const { getFieldById, getFieldsForSection } = useBoundStore(
        useShallow((s) => {
            return {
                getFieldById: s.getFieldById,
                getFieldsForSection: s.getFieldsForSection,
            }
        })
    )
    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )

    const { idItem } = useParams<{
        idItem: string
    }>()
    useEffect(() => {
        query.value = ''
        return () => {
            query.value = ''
        }
    }, [])

    const hasProvider = useMemo(() => {
        const providerData = getObjectById(idItem, values)?.hotelProvider
        if (providerData) {
            return true
        }
        return false
    }, [values, idItem])

    const subHeaderText = () => {
        const headerKey =
            section?.properties?.mobileEditFieldOptions?.headerKeyValue
        if (!headerKey) {
            return getItinTitle()
        }

        const field = getFieldsForSection(section.id)?.find(
            (field: IField) =>
                Number.parseInt(field.id.toString()) === headerKey
        )
        if (!field) {
            return ''
        }
        const keyFieldValue = getValue(field, idItem)

        return keyFieldValue
    }

    const getTitle = () => {
        return subPage.headerText ?? 'Edit List'
    }
    const addButtonText = () => {
        return subPage.addButtonText ?? 'Add Item'
    }
    const sectionData = useMemo(() => {
        const initFieldValue = getValue(field, _id)

        return {
            rendererOptions: subPage.renderOptions,
            data: { data: initFieldValue },
            renderer: 'pastSorter',
            sectionName: section.name,
        } as V2
    }, [field, _id, getValue, subPage.renderOptions])

    const filteredData = useMemo(() => {
        if (!sectionData.data) {
            return sectionData
        }
        if (!sectionData.data.data) {
            return sectionData
        }
        if (!query.value) {
            return sectionData
        }

        const filteredItems = sectionData.data.data.filter((item) => {
            const c_item = structuredCloneL(item)
            if (subPage.userListFieldId) {
                const usersField = getFieldById(subPage.userListFieldId)
                const userList = getValue(usersField, c_item._id, true)
                c_item[usersField?.type_properties?.json_key ?? 'users'] =
                    userList
            }

            return JSON.stringify(c_item)
                .toLowerCase()
                .includes(query.value.toLowerCase())
        })

        return {
            ...sectionData,
            data: {
                data: filteredItems,
            },
        }
    }, [sectionData, query.value])

    const onBackPressed = () => {
        window.goBack()
    }

    const onDelete = async () => {
        updateValues({
            value: [],
            field: field,
            _id: _id,
        })
        clearDialog()
    }

    const trailingItems = () => {
        const popoverItems = []
        if (sectionData.data.data.length > 0) {
            popoverItems.push({
                id: 'deleteAllItems',
                title: i18n.t('edit-itinerary.item.delete-all-bookings'),
                color: ColoursObj.states.error,
                action: () => {
                    replaceDialog({
                        node: (
                            <MobileConfirmDialog
                                header={i18n.t(
                                    'edit-itinerary.item.delete-all',
                                    { sectionName: subHeaderText() }
                                )}
                                text={i18n.t(
                                    'edit-itinerary.item.delete-all-check',
                                    { sectionName: subHeaderText() }
                                )}
                                onConfirm={onDelete}
                                onCancel={() => {
                                    clearDialog()
                                }}
                                cancelText={i18n.t('general.cancel')}
                                confirmText={i18n.t('general.delete')}
                                confirmBtnStyle="destructiveBorderless"
                            />
                        ),
                        backdropClosable: false,
                    })
                },
            })
        }
        if (popoverItems.length > 0) {
            return [
                {
                    id: 'deleteAllBookingsButton',
                    icon: 'More',
                    popoverItems: popoverItems,
                },
            ] as ToolbarItemProps[]
        }

        return []
    }
    return (
        <>
            <NavigationView
                usePageTransition
                navigationBarTitle={getTitle()}
                navigationBarSubtitle={subHeaderText()}
                navigationBarTrailing={trailingItems()}
                backButtonAction={onBackPressed}
            >
                <Gutters>
                    <Box width={'90vw'}>
                        <Stack spacing={'16px'} height={'100%'}>
                            <HeaderSearchBar />
                            <MainButton
                                id={`${subPage.addButtonText}-button`}
                                onClick={() => {
                                    const newId = generateID(
                                        bs().selectedItinID,
                                        section.id
                                    )
                                    createNewItem(newId, _id, field)

                                    window.pushPath(
                                        '/edit/Section/' +
                                            section.id +
                                            '/' +
                                            _id +
                                            '/' +
                                            subPage.onAddPushTo +
                                            '/' +
                                            newId
                                    )
                                }}
                                icon="Add"
                            >
                                {addButtonText()}
                            </MainButton>
                            {hasProvider && (
                                <ManageBookingsButton itemID={idItem} />
                            )}
                            <ErrorWrapper>
                                <ScreenTypeProvider screenType="edit">
                                    <RendererLocator
                                        data={filteredData}
                                        screenType="edit"
                                    />
                                </ScreenTypeProvider>
                            </ErrorWrapper>
                        </Stack>
                    </Box>
                </Gutters>
            </NavigationView>
        </>
    )
}
